import { Box, CircularProgress, Dialog, DialogActions, DialogContent } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useReviewInformation from '@/hooks/useReviewInformation';
import useUser from '@/hooks/useUser';
import { ReviewerInvitationProps } from '@/models/ReviewerInvitation';
import { StateProps } from '@/models/State';
import { UserProps, UserType, userTypeAsString } from '@/models/User';

import { AssignmentSection } from './components/AssignmentSection';
import { NewReviewerForm } from './components/NewReviewerForm';
import { ReviewerPicker } from './components/ReviewerPicker';
import { buildReviewerAssignments } from './utils/buildReviewerAssignments';
import { ReviewerAssignmentButtons } from './utils/ReviewerAssignmentButtons';
import { useReviewerAssignmentForm } from './utils/useReviewerAssignmentForm';

type Props = {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  type?: UserType;
  states: StateProps[];
};

export default function ReviewerModal({ modalOpen, setModalOpen, type, states }: Props) {
  const { usersAll, updateUsersAll, updateUserCurrent, userType } = useContext(UsersContext);

  const { setAlert } = useContext(SnackbarContext);

  const { getReviewerInvitations, postReviewerInvitations, postReviewerAssignments } =
    useReviewInformation();

  const { postUser } = useUser();

  const [reviewers, setReviewers] = useState<ReviewerInvitationProps[] | null>(null);
  const [reviewersLoading, setReviewersLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  // Load reviewers when modal opens
  useEffect(() => {
    if (!modalOpen || type == null) return;

    setReviewersLoading(true);
    setReviewers(null);

    getReviewerInvitations()
      .then((data) => setReviewers(data ?? []))
      .finally(() => setReviewersLoading(false));
  }, [modalOpen, type, getReviewerInvitations]);

  const form = useReviewerAssignmentForm({
    type: type,
    usersAll,
    reviewers,
  });

  const close = () => {
    form.reset();
    setModalOpen(false);
  };

  const showReassign = useMemo(() => {
    const email = (form.reviewerInfo.email ?? '').toLowerCase();
    return userType === UserType.Adf && form.applicantEmails.includes(email);
  }, [userType, form.applicantEmails, form.reviewerInfo.email]);

  const submitAssignments = async () => {
    if (!type) return;

    const email = (form.assignmentInfo.email ?? '').toLowerCase();
    if (!email || !userType) return;

    // Invite if new reviewer
    if (form.newReviewer) {
      await postReviewerInvitations({ ...form.reviewerInfo, userType: type });
    }

    const assignments = buildReviewerAssignments({
      type,
      email,
      programIds: form.selectedPrograms,
      countryIds: form.selectedCountries,
      countyIds: form.selectedCounties,
      stateId: form.selectedState,
      countryId: form.assignmentInfo.countryId,
    });

    await Promise.all(assignments.map(postReviewerAssignments));

    setReviewers((prev) => [
      ...(prev ?? []),
      {
        email: form.assignmentInfo.email || form.reviewerInfo.email || '',
        firstName: form.reviewerInfo.firstName || '',
        lastName: form.reviewerInfo.lastName || '',
        userType: userType,
      } satisfies ReviewerInvitationProps,
    ]);
  };

  function patchUserInList(all: UserProps[] | null | undefined, updated: UserProps) {
    if (!all?.length) return all ?? [];
    const idx = all.findIndex((u) => u.b2CId === updated.b2CId);
    if (idx < 0) return all;

    const copy = [...all];
    copy[idx] = { ...copy[idx], ...updated };
    return copy;
  }

  const submit = async () => {
    try {
      setPosting(true);

      if (showReassign) {
        // ADF-only reassignment flow
        const targetEmail = (form.reviewerInfo.email ?? '').toLowerCase();
        const applicant = usersAll?.find(
          (u: UserProps) => (u.email ?? '').toLowerCase() === targetEmail
        );
        if (!applicant) return;

        const updatedUser: UserProps = {
          ...applicant,
          userType: type!,
          validated: true,
          admin: true,
          disabled: false,
        };

        const saved = await postUser(updatedUser);

        updateUserCurrent(saved);
        // usage
        const next = patchUserInList(usersAll, saved);
        updateUsersAll(next);
      }

      try {
        await submitAssignments();
        close();
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'There was an error submitting the reviewer assignments.';
        console.error('Error submitting assignments:', error);
        // Handle error appropriately, e.g., show notification to user
        setAlert({
          severity: 'error',
          message: message,
        });
      }
    } finally {
      setPosting(false);
    }
  };

  if (!type) return null;
  const showReviewerLoader = reviewersLoading || !reviewers?.length;

  return (
    <Dialog open={modalOpen} onClose={close} maxWidth='md' fullWidth>
      <DialogTitleWithClose onClose={close}>Assign {userTypeAsString(type)}</DialogTitleWithClose>

      <DialogContent>
        {showReviewerLoader ? (
          <Box display='flex' justifyContent='center' py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <ReviewerPicker
              reviewers={reviewers ?? []}
              type={type}
              value={form.selectedReviewerEmail}
              onChange={form.handleReviewerSelect}
            />

            {form.newReviewer && (
              <NewReviewerForm
                reviewerInfo={form.reviewerInfo}
                onChange={form.handleReviewerInfoChange}
                applicantEmails={form.applicantEmails}
                reviewerEmails={form.reviewerEmails}
              />
            )}

            {form.selectedReviewerEmail && (
              <AssignmentSection
                type={type}
                states={states}
                selectedPrograms={form.selectedPrograms}
                onProgramsChange={form.handleProgramsChange}
                selectedCountries={form.selectedCountries}
                onCountriesChange={form.handleCountriesChange}
                selectedState={form.selectedState}
                onStateChange={form.handleStateChange}
                selectedCounties={form.selectedCounties}
                onCountiesChange={form.handleCountiesChange}
                countiesInState={form.countiesInState}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <ReviewerAssignmentButtons
          onCancel={close}
          onSubmit={submit}
          onReassign={submit} // single submit handles both paths
          showReassign={showReassign}
          disabled={form.isSubmitDisabled}
          posting={posting}
        />
      </DialogActions>
    </Dialog>
  );
}
